import apiServices from 'config/apiServiceApiGateway'

const { Get, Post, Put, Patch, Delete } = apiServices

export type AdjacentLocationType = {
  location_id: number
  location_name: string
}

export type AdjacentHubItemType = {
  location_id: number
  // NOTE: backend's protobuf contract types this as int64, but it holds the hub's
  // display name (contract bug per Adjacent Service API Admin doc on Confluence).
  location_name: string
  location_type: string
  status: boolean
  // in KM
  max_distance: number
  adjacent_location: AdjacentLocationType[]
  // in minutes, can be negative
  max_additional_sla: number
  // in percent
  quota_allowed: number
  max_driver_ratio: number
  max_picker_ratio: number
}

export type GetAdjacentHubListResponseType = {
  data: {
    adjacent: AdjacentHubItemType[]
  }
  pagination: {
    page: number
    limit: number
    total: number
  }
}

export type GetAdjacentHubListRequestType = {
  params: {
    page: number
    limit: number
    location_id?: number | string
    location_name?: string
    status?: boolean
    sort?: string
  }
}

export const getAdjacentHubList = ({ params }: GetAdjacentHubListRequestType) =>
  Get<GetAdjacentHubListResponseType, true>({
    url: `/location/internal/v1/adjacent`,
    params,
  })

export type GetAdjacentHubDetailResponseType = {
  data: {
    location_id: number
    location_name: string
    max_distance: number
    max_driver_ratio: number
    max_picker_ratio: number
    max_additional_sla: number
    quota_allowed: number
    adjacent_location: AdjacentLocationType[]
  }
}

export const getAdjacentHubDetail = (locationId: number) =>
  Get<GetAdjacentHubDetailResponseType, true>({
    url: `/location/internal/v1/adjacent/${locationId}`,
  })

export type AdjacentConfigType = {
  title: string
  description: string
  // close reasons that allow rerouting orders to nearby hubs
  close_reason_id: number[]
}

export type GetAdjacentConfigResponseType = {
  data: AdjacentConfigType
}

export const getAdjacentConfig = () =>
  Get<GetAdjacentConfigResponseType, true>({
    url: `/location/internal/v1/adjacent/meta/config`,
  })

export type AdjacentHubPayloadType = {
  location_id: number
  // in KM, same unit as the list response
  max_distance: number
  adjacent_location_ids: number[]
  max_driver_ratio: number
  max_picker_ratio: number
  // in minutes
  max_additional_sla: number
  // in percent
  quota_allowed: number
}

export type PostAdjacentHubRequestType = {
  payload: AdjacentHubPayloadType
}

// Shared by create, update, update status and delete.
export type AdjacentHubMutationResponseType = {
  data: {
    message: string
  }
  error: {
    message: string
    code: number
    status: boolean
  }
}

export const postAdjacentHub = ({ payload }: PostAdjacentHubRequestType) =>
  Post<AdjacentHubMutationResponseType, true>({
    url: `/location/internal/v1/adjacent`,
    data: payload,
  })

export type PutAdjacentHubRequestType = {
  payload: AdjacentHubPayloadType
}

export const putAdjacentHub = ({ payload }: PutAdjacentHubRequestType) =>
  Put<AdjacentHubMutationResponseType, true>({
    url: `/location/internal/v1/adjacent/${payload.location_id}`,
    data: payload,
  })

export type PatchAdjacentHubStatusRequestType = {
  payload: {
    location_id: number
    status: boolean
  }
}

export const patchAdjacentHubStatus = ({ payload }: PatchAdjacentHubStatusRequestType) =>
  Patch<AdjacentHubMutationResponseType, true>({
    url: `/location/internal/v1/adjacent/status/${payload.location_id}`,
    data: payload,
  })

export const deleteAdjacentHub = (locationId: number) =>
  Delete<AdjacentHubMutationResponseType, true>({
    url: `/location/internal/v1/adjacent/${locationId}`,
  })
